/**
 * 브라우저 쪽 아바타 사전 검사.
 *
 * **이건 방어선이 아니다.** 브라우저를 거치지 않고 API 를 직접 부르면 그만이다.
 * 진짜 방어는 서버(재인코딩 + Rekognition + 횟수 제한)이고, 여기는 정상 사용자가
 * 크롭까지 끝내고 나서 서버에 거부당하는 헛수고를 없애는 UX 다.
 * 그래서 판정이 불확실하면 **통과시키고 서버에 맡긴다.**
 *
 * 콘텐츠(노출 등) 판정은 여기 없다 — NSFWJS v4 는 모델을 UMD `.min.js` 로 안고 있어
 * CRA 의 webpack 이 정적 분석에 실패한다(`Cannot statically analyse 'require(…, …)'`).
 * 넣으려면 TF.js 모델 파일을 `public/` 에 자체 호스팅하고 `nsfwjs/core` 로 URL 로드해야 한다.
 * 그건 별도 작업이고, 없다고 해서 막히는 이미지가 늘거나 줄지 않는다 — 판정은 서버가 한다.
 */
export type ImageCheckResult = { ok: true } | { ok: false; message: string }

const MAX_BYTES = 8 * 1024 * 1024
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
/** 서버 상한(5천만)과 같은 선 */
const MAX_PIXELS = 50_000_000

/** 파일을 고른 직후. 브라우저가 열지 못하는 파일은 서버도 못 연다 */
export const checkPickedFile = async (file: File): Promise<ImageCheckResult> => {
	if (file.size > MAX_BYTES) {
		return { ok: false, message: 'This file is too large. The limit is 8MB.' }
	}

	if (file.type !== '' && !ALLOWED_TYPES.includes(file.type)) {
		return {
			ok: false,
			message: 'Choose a JPEG, PNG, WebP or GIF image.',
		}
	}

	const size = await readImageSize(file)

	if (size === null) {
		return { ok: false, message: 'This file is not a readable image.' }
	}

	if (size.width * size.height > MAX_PIXELS) {
		return { ok: false, message: 'This image is too large to process.' }
	}

	return { ok: true }
}

const readImageSize = (file: File): Promise<{ width: number; height: number } | null> =>
	new Promise(resolve => {
		const objectUrl = URL.createObjectURL(file)
		const img = new Image()

		img.onload = () => {
			URL.revokeObjectURL(objectUrl)
			resolve({ width: img.naturalWidth, height: img.naturalHeight })
		}
		img.onerror = () => {
			URL.revokeObjectURL(objectUrl)
			resolve(null)
		}
		img.src = objectUrl
	})
